import { Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, of, throwError, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Internal } from '../decorators/app.decorators';
import { VCPM } from 'pcs-domain';
import { AuthState } from '../../store/auth/auth.state';
import { AuthService } from './auth/auth.service';
import { VCPMService } from './vcpm.service';
import { AlertService } from './alert/alert.service';
import { UserSessionManagementService } from './user-session-management/user-session-management.service';
import { RefreshProjectCollaboration } from '../../store/project-details/project-details.actions';

/**
 * Service to handle project collaboration helper methods
 * Provides utilities to check lock states, view-only access, and ownership
 */
@Internal()
@Injectable({
    providedIn: 'root'
})
export class ProjectCollaborationService {
    private store = inject(Store);
    private authService = inject(AuthService);
    private vcpmService = inject(VCPMService);
    private alertService = inject(AlertService);
    private userSessionService = inject(UserSessionManagementService);
    private readonly editAccessLost = new Subject<string>();
    readonly editAccessLost$ = this.editAccessLost.asObservable();
    private readonly editorGate = signal<{ projectId: string | null; blocked: boolean }>({ projectId: null, blocked: false });

    setEditorBlocked(projectId: string | null, blocked: boolean): void {
        this.editorGate.set({ projectId, blocked });
    }

    isEditorBlocked(project: VCPM.Project): boolean {
        const gate = this.editorGate();
        return gate.projectId === String(project.projectId) && gate.blocked;
    }

    private get loggedUserEmail(): string {
        return this.store.selectSnapshot(AuthState.getLoggedInUserProfile)?.acct || '';
    }

    private get loggedUserEmailFromIdToken(): string {
        try {
            const claims = this.authService.getIdTokenPayload?.();
            return claims?.email || claims?.acct || claims?.user_loginid || this.authService.getUserEmail() || '';
        } catch { return ''; }
    }

    private authenticatedAliases(): string[] {
        const profile = this.store.selectSnapshot(AuthState.getLoggedInUserProfile);
        let claims: any;
        try { claims = this.authService.getIdTokenPayload?.(); } catch { claims = undefined; }
        return [profile?.acct, profile?.email, profile?.user_loginid, ...(profile?.identityAliases ?? []), claims?.email, claims?.acct, claims?.user_loginid,
            this.loggedUserEmailFromIdToken].filter((value): value is string => typeof value === 'string' && !!value.trim())
            .map(value => value.trim().toLowerCase());
    }

    /** Check the loaded collaboration state before a write, without fetching the project. */
    validateBeforeWrite(project: VCPM.Project | undefined): Observable<boolean> {
        if (!project || this.editorGate().projectId !== String(project.projectId)) return of(true);
        if (this.isEditorBlocked(project)) return throwError(() => new Error('Editing is unavailable until your lock is confirmed.'));
        if (!this.canEditProject(project) || this.isAnalysisLocked(project) ||
            (this.isAnalysisNeedLock(project) && (!project.locked || !this.isCurrentUserEmail(project.lockedBy)))) {
            this.setEditorBlocked(String(project.projectId), true);
            this.editAccessLost.next(String(project.projectId));
            return throwError(() => new Error('Your lock or edit access has changed.'));
        }
        return of(true);
    }

    canEditProject(project: VCPM.Project): boolean {
        if (this.isProjectOwner(project, this.loggedUserEmail)) return true;
        return !!project.sharingData?.some(share => share.shareType === 'collaborate' && this.isCurrentUserEmail(share.toUser));
    }

    constructor() {
        // Empty constructor - properties are lazy-loaded via getters
    }

    /**
     * Compare two emails, checking against both acct and id_token email
     * Handles cases where users have multiple email addresses
     * @param email1 - First email (typically from project.lockedBy)
     * @param email2 - Second email (typically current user's email) - OPTIONAL
     * @returns true if emails match
     */
    private emailsMatch(email1: string | null | undefined, email2?: string | null | undefined): boolean {
        if (!email1) {
            return false;
        }
        
        const normalizedEmail1 = email1.trim().toLowerCase();
        const acctEmail = this.loggedUserEmail;
        const idTokenEmail = this.loggedUserEmailFromIdToken;
        const normalizedAcct = acctEmail.trim().toLowerCase();
        const normalizedIdToken = idTokenEmail ? idTokenEmail.trim().toLowerCase() : '';
        
        // Helper: check if a given email belongs to the current user
        const isCurrentUser = (email: string): boolean => {
            if (!email) return false;
            if (email === normalizedAcct) return true;
            if (normalizedIdToken && email === normalizedIdToken) return true;
            const username = email.split('@')[0];
            if (username === normalizedAcct.split('@')[0]) return true;
            if (normalizedIdToken && username === normalizedIdToken.split('@')[0]) return true;
            return this.authenticatedAliases().some(alias => email === alias || username === alias.split('@')[0]);
        };
        
        if (email2) {
            // Two-argument mode: check if email1 and email2 represent the same person
            const normalizedEmail2 = email2.trim().toLowerCase();
            
            // Direct match
            if (normalizedEmail1 === normalizedEmail2) {
                return true;
            }
            
            // Username match
            if (normalizedEmail1.split('@')[0] === normalizedEmail2.split('@')[0]) {
                return true;
            }
            
            // Both belong to the current user (e.g., acct email vs id_token email)
            if (isCurrentUser(normalizedEmail1) && isCurrentUser(normalizedEmail2)) {
                return true;
            }
            
            return false;
        }
        
        // Single-argument mode: check if email1 belongs to the current user
        // Preserve the historical username fallback for aliases across domains.
        return isCurrentUser(normalizedEmail1);
    }

    /**
     * Check if analysis/project is locked by ANOTHER user (not current user)
     * @param project - The project to check
     * @returns true if project is locked by someone else
     */
    isAnalysisLocked(project: VCPM.Project | undefined): boolean {
        if (!project || !project.locked || !project.lockedBy) {
            return false;
        }

        // Check if locked by current user using enhanced email matching
        const isLockedByMe = this.emailsMatch(project.lockedBy, this.loggedUserEmail);

        // If locked by current user, it's not "locked" from their perspective
        if (isLockedByMe) {
            return false;
        }

        // Locked by another user
        return true;
    }

    /**
     * Check if project needs locking mechanism (has collaborate share type)
     * @param project - The project to check
     * @returns true if project has any 'collaborate' share type
     */
    isAnalysisNeedLock(project: VCPM.Project | undefined): boolean {
        if (!project || !project.sharingData || project.sharingData.length === 0) {
            return false;
        }

        // Lock needed if shared with 'collaborate' type
        return project.sharingData.some((data: VCPM.SharingData) => 
            data.shareType === 'collaborate'
        );
    }

    /**
     * Check if current user has view-only access to the project
     * @param project - The project to check
     * @returns true if current user has view-only access
     */
    isAnalysisSharedViewOnly(project: VCPM.Project | undefined): boolean {
        if (!project || !project.sharingData || project.sharingData.length === 0) {
            return false;
        }

        // Find if current user is in sharingData as view-only (using enhanced email matching)
        const sharedToCurrentUser = project.sharingData.find((share: VCPM.SharingData) => 
            this.emailsMatch(share.toUser)
        );

        return sharedToCurrentUser?.shareType === 'viewOnly';
    }

    /**
     * Check if forms should be disabled (locked by another user or view-only)
     * @param project - The project to check
     * @returns true if forms should be disabled
     */
    checkDisableFormForLockedAnalysis(project: VCPM.Project | undefined): boolean {
        if (!project) {
            return false;
        }

        if (this.isEditorBlocked(project)) return true;

        // Disable if locked by another user
        if (this.isAnalysisLocked(project)) {
            return true;
        }

        // Disable if user has view-only access
        if (this.isAnalysisSharedViewOnly(project)) {
            return true;
        }

        return false;
    }

    /**
     * Build list of collaborators with their roles
     * @param project - The project
     * @returns Array of collaborator objects with email and role
     */
    buildCollaboratorsList(project: VCPM.Project): { email: string; role: string }[] {
        const collaborators: { email: string; role: string }[] = [];
        const shareHistory = project.sharingData;

        if (shareHistory && shareHistory.length > 0) {
            // currentOwner has the full email; createdBy stores only the username
            const ownerEmail = project.currentOwner || shareHistory[0].fromUser || project.createdBy;
            if (ownerEmail) {
                collaborators.push({ email: ownerEmail, role: 'Owner' });
            }

            for (const element of shareHistory) {
                // Add collaborators/viewers if not already the owner or already added
                // Use emailsMatch to handle username-vs-email comparisons (e.g. createdBy lacks domain)
                if (!this.emailsMatch(element.toUser, ownerEmail) && !collaborators.some(c => this.emailsMatch(c.email, element.toUser))) {
                    let userRole = '';
                    if (element.shareType === 'viewOnly') {
                        userRole = 'View Only';
                    } else if (element.shareType === 'collaborate') {
                        userRole = 'Collaborator';
                    } else if (element.shareType === 'shareCopy') {
                        // For shareCopy, skip as they're not collaborators
                        continue;
                    }
                    collaborators.push({ email: element.toUser, role: userRole });
                }
            }
        }
        return collaborators;
    }

    /**
     * Lock or unlock a project
     * @param projectId - The project ID
     * @param action - 'lock' or 'unlock'
     * @param isAdminUserRequest - Whether this is an admin request
     * @returns Observable of the API call
     */
    lockUnlockAnalysis(projectId: string, action: 'lock' | 'unlock', isAdminUserRequest: boolean = false): Observable<any> {
        return this.vcpmService.lockUnlockProject(projectId, action, isAdminUserRequest).pipe(
            tap((response: VCPM.Project) => {
                this.store.dispatch(new RefreshProjectCollaboration(response));
            }),
            catchError(error => {
                this.alertService.showAlert('danger', `Failed to ${action} project: ${error.message}`, true, 3000);
                return of(null);
            })
        );
    }

    /**
     * Unshare a project from a user
     * @param projectId - The project ID
     * @param userEmail - Email of user to unshare from
     * @returns Observable of the API call
     */
    unshareAnalysis(projectId: string, userEmail: string): Observable<any> {
        return this.vcpmService.unshareProject(projectId, userEmail).pipe(
            tap(() => {
                this.alertService.showAlert('success', `Project unshared from ${userEmail} successfully.`, true, 3000);
            }),
            catchError(error => {
                this.alertService.showAlert('danger', `Failed to unshare project: ${error.message}`, true, 10000);
                return of(null);
            })
        );
    }

    /**
     * Notify collaborators about project updates
     * @param project - The project
     * @returns Observable of the API call
     */
    notifyCollaborators(project: VCPM.Project): Observable<any> {
        if (this.emailsMatch(project.lockedBy, this.loggedUserEmail) && 
            project.sharingData && 
            project.sharingData.some(s => s.shareType === 'collaborate')) {
            const emailPayload = {
                projectId: project.projectId,
                userName: this.authService.getUserFullName(),
                currentUserEmail: this.loggedUserEmail || this.loggedUserEmailFromIdToken || '',
                accountName: project.metadata.companyName,
                sharingData: project.sharingData.filter(s => 
                    s.shareType === 'collaborate' && 
                    !this.emailsMatch(s.toUser, this.loggedUserEmail)
                )
            };
            if (emailPayload.sharingData.length > 0) {
                return this.vcpmService.notifySharedUsers(emailPayload);
            }
        }
        return of(null);
    }

    /**
     * Check if two emails match, considering both acct and id_token emails
     * Public method that can be used by components
     * @param email1 - First email
     * @param email2 - Second email
     * @returns true if emails match
     */
    doEmailsMatch(email1: string | null | undefined, email2: string | null | undefined): boolean {
        return this.emailsMatch(email1, email2);
    }

    /**
     * Check if the given email belongs to the current logged-in user
     * Checks against both acct and id_token email
     * @param email - Email to check
     * @returns true if email belongs to current user
     */
    isCurrentUserEmail(email: string | null | undefined): boolean {
        if (!email) {
            return false;
        }
        
        // Use emailsMatch with only email1 to check against current user's emails
        return this.emailsMatch(email);
    }

    // ===== Additional helper methods for backwards compatibility =====

    /**
     * Check if project is locked by another user
     * @param project - The project to check
     * @param currentUserEmail - Email of the current user
     * @returns true if project is locked by another user
     */
    isProjectLocked(project: VCPM.Project | undefined, currentUserEmail: string): boolean {
        if (!project || !project.locked || !project.lockedBy) {
            return false;
        }

        // If current user is the one who locked it, return false (they can edit)
        if (this.emailsMatch(project.lockedBy, currentUserEmail)) {
            return false;
        }

        // Locked by another user
        return true;
    }

    /**
     * Check if project needs locking mechanism (has collaborate share type)
     * @param project - The project to check
     * @returns true if project has any 'collaborate' share type
     */
    isProjectNeedLock(project: VCPM.Project | undefined): boolean {
        return this.isAnalysisNeedLock(project);
    }

    /**
     * Check if project is shared as view-only to current user
     * @param project - The project to check
     * @param currentUserEmail - Email of the current user
     * @returns true if current user has view-only access
     */
    isProjectViewOnly(project: VCPM.Project | undefined, currentUserEmail: string): boolean {
        if (!project || !project.sharingData || project.sharingData.length === 0) {
            return false;
        }

        // Find if current user is shared as view-only (using enhanced email matching)
        const sharedToCurrentUser = project.sharingData.find((share: VCPM.SharingData) => 
            this.emailsMatch(share.toUser, currentUserEmail)
        );

        return sharedToCurrentUser?.shareType === 'viewOnly';
    }

    /**
     * Check if current user is the project owner
     * @param project - The project to check
     * @param currentUserEmail - Email of the current user
     * @returns true if current user is the owner
     */
    isProjectOwner(project: VCPM.Project | undefined, currentUserEmail: string): boolean {
        if (!project) {
            return false;
        }

        // Check if user is the creator or current owner (using enhanced email matching)
        return this.emailsMatch(project.currentOwner || project.sharingData?.[0]?.fromUser || project.createdBy, currentUserEmail);
    }

    /**
     * Check if forms should be disabled for the current user
     * @param project - The project to check
     * @param currentUserEmail - Email of the current user
     * @returns true if forms should be disabled (locked by another user or view-only)
     */
    shouldDisableForms(project: VCPM.Project | undefined, currentUserEmail: string): boolean {
        if (!project) {
            return false;
        }

        if (this.isEditorBlocked(project)) return true;

        // Disable if locked by another user
        if (this.isProjectLocked(project, currentUserEmail)) {
            return true;
        }

        // Disable if user has view-only access
        if (this.isProjectViewOnly(project, currentUserEmail)) {
            return true;
        }

        return false;
    }

    /**
     * Check if current user is the one who locked the project
     * @param project - The project to check
     * @param currentUserEmail - Email of the current user
     * @returns true if current user locked the project
     */
    isLockedByCurrentUser(project: VCPM.Project | undefined, currentUserEmail: string): boolean {
        if (!project || !project.locked || !project.lockedBy) {
            return false;
        }

        return this.emailsMatch(project.lockedBy, currentUserEmail);
    }

    /**
     * Get the role of current user for the project
     * @param project - The project
     * @param currentUserEmail - Email of the current user
     * @returns 'Owner', 'Collaborator', 'View Only', or 'None'
     */
    getUserRole(project: VCPM.Project | undefined, currentUserEmail: string): string {
        if (!project) {
            return 'None';
        }

        // Check if owner
        if (this.isProjectOwner(project, currentUserEmail)) {
            return 'Owner';
        }

        // Check sharing data (using enhanced email matching)
        if (project.sharingData && project.sharingData.length > 0) {
            const share = project.sharingData.find((s: VCPM.SharingData) => 
                this.emailsMatch(s.toUser, currentUserEmail)
            );

            if (share) {
                switch (share.shareType) {
                    case 'collaborate':
                        return 'Collaborator';
                    case 'viewOnly':
                        return 'View Only';
                    case 'shareCopy':
                        return 'Owner'; // User became owner of the copy
                    default:
                        return 'None';
                }
            }
        }

        return 'None';
    }
}
